import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { Modal, message } from "antd";
import { observer } from "mobx-react-lite";
import { globalModel } from "../../global/GlobalModel";
import { LobbyModel } from "../lobby/LobbyModel";
import { RelatedSpacesFollowsPanel } from "../lobby/RelatedSpacesFollowsPanel";
import type { ListTab } from "../lobby/relatedSpacesFollowsLogic";
import { syncOrdersFallbackFromRelatedSpaceCards } from "../lobby/syncOrdersFallbackFromCards";
import { MyOrdersPanel } from "../order/MyOrdersPanel";

const ID8 = /^\d{8}$/;

/** 下拉「我的订单」是否展示等的同一套优先级 */
export function resolveOrdersSpaceId(preferred?: string | null): string | null {
  const p = preferred?.trim() ?? "";
  if (ID8.test(p)) return p;
  const fromRecent = globalModel.recentSpaceEntries
    .map((e) => e.id.trim())
    .find((sid) => ID8.test(sid));
  if (fromRecent) return fromRecent;
  const fb = globalModel.ordersMenuFallbackSpaceId;
  return fb && ID8.test(fb) ? fb : null;
}

export type RelatedSpacesOrdersModalsApi = {
  openRelatedSpaces: (opts?: { initialTab?: ListTab }) => void;
  openOrdersSpace: (opts?: { spaceId?: string | null }) => void;
  relatedModalOpen: boolean;
  relatedModalTab: ListTab;
};

const Ctx = createContext<RelatedSpacesOrdersModalsApi | null>(null);

export function useRelatedSpacesOrdersModals(): RelatedSpacesOrdersModalsApi {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error("useRelatedSpacesOrdersModals must be used inside provider");
  }
  return v;
}

const MODAL_BODY_PAD = 24;
const MODAL_HEADER_PAD_X = 24;

/** flex 柱状弹框：头部单独 padding，避免标题贴着弹框内容上沿 */
const shellModalStyles = {
  content: {
    height: "80vh",
    maxHeight: "80vh",
    borderRadius: 0,
    padding: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
  },
  header: {
    flexShrink: 0,
    margin: 0,
    padding: `20px ${MODAL_HEADER_PAD_X}px 16px`,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    padding: MODAL_BODY_PAD,
    boxSizing: "border-box" as const,
  },
};

const OrdersModalBody = observer((props: { spaceId: string }) => (
  <MyOrdersPanel spaceId={props.spaceId} />
));

/** 全站「与我相关的空间」「我的订单」弹框；弹框可视高度固定为 80vh（内容区内滚动） */
export function RelatedSpacesOrdersModalsProvider(props: {
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [relatedOpen, setRelatedOpen] = useState(false);
  const [relatedTab, setRelatedTab] = useState<ListTab>("follow");
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [ordersSpaceId, setOrdersSpaceId] = useState("");

  const followsLobbyModel = useMemo(
    () => new LobbyModel("follows", "00000000"),
    []
  );

  useEffect(() => {
    if (!relatedOpen) return;
    void followsLobbyModel.load();
  }, [relatedOpen, followsLobbyModel]);

  useEffect(() => {
    if (!relatedOpen || !followsLobbyModel.loaded) return;
    syncOrdersFallbackFromRelatedSpaceCards(followsLobbyModel.cards);
  }, [relatedOpen, followsLobbyModel.loaded, followsLobbyModel.cards]);

  const openRelatedSpaces = useCallback((opts?: { initialTab?: ListTab }) => {
    const t = opts?.initialTab ?? "follow";
    setRelatedTab(t);
    setRelatedOpen(true);
  }, []);

  const openOrdersSpace = useCallback((opts?: { spaceId?: string | null }) => {
    const sid = resolveOrdersSpaceId(opts?.spaceId ?? null);
    if (!sid) {
      message.info(
        "暂无可查看订单的空间上下文，请先从顶栏进入某空间或在「与我相关的空间」里选择"
      );
      return;
    }
    setOrdersSpaceId(sid);
    setOrdersOpen(true);
  }, []);

  const api = useMemo<RelatedSpacesOrdersModalsApi>(
    () => ({
      openRelatedSpaces,
      openOrdersSpace,
      relatedModalOpen: relatedOpen,
      relatedModalTab: relatedTab,
    }),
    [openRelatedSpaces, openOrdersSpace, relatedOpen, relatedTab]
  );

  return (
    <Ctx.Provider value={api}>
      {props.children}
      <Modal
        title="与我相关的空间"
        open={relatedOpen}
        onCancel={() => setRelatedOpen(false)}
        footer={null}
        width={980}
        centered
        destroyOnClose
        zIndex={1100}
        styles={shellModalStyles}
      >
        <RelatedSpacesFollowsPanel
          model={followsLobbyModel}
          listTab={relatedTab}
          onTabChange={setRelatedTab}
          onCardNavigate={(id) => {
            setRelatedOpen(false);
            navigate(`/space/${id}/home`);
          }}
          onEnterSpaceIdHome={() => {
            setRelatedOpen(false);
            navigate("/");
          }}
          showPageHeading={false}
        />
      </Modal>
      <Modal
        title="我的订单"
        open={ordersOpen}
        onCancel={() => setOrdersOpen(false)}
        footer={null}
        width={920}
        centered
        destroyOnClose
        zIndex={1100}
        styles={shellModalStyles}
      >
        {ordersSpaceId !== "" ? <OrdersModalBody spaceId={ordersSpaceId} /> : null}
      </Modal>
    </Ctx.Provider>
  );
}
